import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient, getErrorMessage } from '@/services/api';
import { getNotificationPreferences, updateNotificationPreferences, deleteAllNotifications } from '@/services/notifications.api';
import { showSuccess, showError } from '@/utils/toast';

export function useUnreadCount() {
  return useQuery({
    queryKey: ['unreadCount'],
    queryFn: async () => {
      const res = await apiClient.get('/notifications/unread-count');
      return res.data.data;
    },
    refetchInterval: 30_000,
  });
}

const TAB_TYPE_MAP: Record<string, string> = {
  orders: 'new_order,order_status_changed',
  stock: 'low_stock,product_backorder',
  system: 'system,ai_insight,subscription_expiring,subscription_expired,plugin_required,bridge_offline',
};

export function useNotifications(filter?: string, siteFilter?: string | null) {
  return useQuery({
    queryKey: ['notifications', filter, siteFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filter === 'unread') {
        params.set('isRead', 'false');
      } else if (filter && TAB_TYPE_MAP[filter]) {
        params.set('types', TAB_TYPE_MAP[filter]);
      }
      if (siteFilter) {
        params.set('siteId', siteFilter);
      }
      const res = await apiClient.get(`/notifications?${params}`);
      return res.data.data?.items ?? res.data.data ?? [];
    },
  });
}

export function useRecentNotifications() {
  return useQuery({
    queryKey: ['recentNotifications'],
    queryFn: async () => {
      const res = await apiClient.get('/notifications?perPage=5');
      return res.data.data?.items ?? res.data.data ?? [];
    },
    refetchInterval: 30_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string) => {
      await apiClient.put(`/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['unreadCount'] });
      qc.invalidateQueries({ queryKey: ['recentNotifications'] });
    },
  });
}

export function useMarkAllRead() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (siteId?: string) => {
      const params = siteId ? `?siteId=${siteId}` : '';
      await apiClient.put(`/notifications/read-all${params}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['unreadCount'] });
      qc.invalidateQueries({ queryKey: ['recentNotifications'] });
      showSuccess(t('common.success'));
    },
    onError: (err) => showError(getErrorMessage(err)),
  });
}

export function useDeleteAllNotifications() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (siteId?: string) => deleteAllNotifications(siteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['unreadCount'] });
      qc.invalidateQueries({ queryKey: ['recentNotifications'] });
      showSuccess(t('notifications.allDeleted'));
    },
    onError: (err) => showError(getErrorMessage(err)),
  });
}

export function useNotificationPreferences(siteId: string | null) {
  return useQuery({
    queryKey: ['notificationPreferences', siteId],
    queryFn: () => getNotificationPreferences(siteId!),
    enabled: !!siteId,
  });
}

export function useUpdateNotificationPreferences(siteId: string | null) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: Record<string, boolean>) => updateNotificationPreferences(siteId!, prefs),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notificationPreferences', siteId] });
      showSuccess(t('notifications.prefsSaved'));
    },
    onError: (err) => showError(getErrorMessage(err)),
  });
}
